import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Legend, CartesianGrid, LabelList,
} from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import HoverTooltip from '../../shared/HoverTooltip';
import { getRiCorridors } from '../../../api/auth';
import { RiLoading, RiEmpty, RiError, RiSparseNotice, RiPagination } from '../primitives/States';
import { useReload } from '../lib/loadState';
import { formatCorridorLabel } from '../lib/labelFormat';
import { fmtUsd } from '../../../utils/format';
import { RI_TABLE_FRAME, RI_THEAD, RI_TH, RI_ROW, RI_TD } from '../lib/tableStyles';

const TABLE_PAGE_SIZE = 25;

// Format float columns to a fixed decimal so e.g. an average that lands on
// "18" doesn't render next to others showing "12.5".
const fmt1 = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(1));

function convColor(pct) {
  if (pct == null) return 'text-gray-400';
  if (pct > 5) return 'text-emerald-600 dark:text-emerald-400';
  if (pct >= 1) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

// Derive Conv % directly from the row's bookings/volume so the displayed
// number always reconciles with the two columns to its left. Previously
// we surfaced the API's `conversion_pct` field, which used a different
// denominator and made the column look inconsistent (markup-comment #53).
function convPctFromRow(r) {
  const v = Number(r?.volume) || 0;
  const b = Number(r?.bookings) || 0;
  return v > 0 ? (b / v) * 100 : 0;
}

// `conversion_note` the backend sets on a corridor that has no booking sessions at all:
// its Conv % and CCE are undefined rather than zero, so they render as a dash.
const NO_BOOKING_SESSIONS = 'no_booking_sessions';

/**
 * A grey dash with a hover explanation, for a figure the corridor cannot have yet.
 * @returns {import('react').ReactNode}
 */
function NoSessionsCell() {
  return (
    <HoverTooltip
      triggerClassName="cursor-help text-gray-400 dark:text-gray-500"
      content="No booking sessions from this corridor yet"
    >
      —
      {/* Gives screen-reader users the reason. The hover tooltip is mouse-only, so sighted
          keyboard and touch users see the dash without an explanation. */}
      <span className="sr-only">No booking sessions from this corridor yet</span>
    </HoverTooltip>
  );
}

// Route count below which a corridor is flagged "Emerging". The backend tags
// a corridor 'emerging' when it has 2–4 observed routes and 'established' at 5+
// (the route count is surfaced as the row's `volume` field).
const EMERGING_ROUTE_THRESHOLD = 5;

// Rich popover copy for the corridor "Emerging" badge — mirrors the AIM
// Competitive Threat tooltip: lead with the term being defined, explain why
// the corridor is flagged, that its numbers are early estimates, and how many
// more routes graduate it.
function emergingTooltipContent(r) {
  const routes = Number(r?.volume ?? 0);
  const remaining = Math.max(0, EMERGING_ROUTE_THRESHOLD - routes);
  return (
    <>
      <strong className="text-amber-300">Emerging</strong> — <strong>{formatCorridorLabel(r.corridor)}</strong> is built from{' '}
      {routes > 0 ? (
        <>
          only <strong className="text-amber-300">{routes}</strong> observed route{routes === 1 ? '' : 's'}
        </>
      ) : (
        <>only a handful of observed routes</>
      )}
      {' '}— below the <strong>{EMERGING_ROUTE_THRESHOLD}-route</strong> threshold that graduates a corridor from Emerging to the regularly-tracked pool.
      {' '}Its metrics (volume, revenue/CCE, conversion) are early estimates and may shift as more guest trips are recorded.
      {remaining > 0 && (
        <>
          {' '}
          <strong className="text-amber-300">{remaining}</strong> more route{remaining === 1 ? '' : 's'} will move it into regular tracking.
        </>
      )}
    </>
  );
}

// Tracks the viewport width so chart YAxis widths/fonts/truncation can scale
// with the screen. Same pattern as CongestionTab and TripEconomicsTab.
function useViewportWidth() {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
}

export default function CorridorsTab({ clientId, range, updateParam, searchParams }) {
  const from = range?.from ?? null;
  const to = range?.to ?? null;
  const sort = searchParams.get('sort') === 'cce' ? 'cce' : 'volume';
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, retry] = useReload();
  const [page, setPage] = useState(1);
  const vw = useViewportWidth();
  const yAxisWidth = vw < 640 ? 120 : vw < 1024 ? 180 : 240;
  const yAxisFont = vw < 640 ? 10 : 11;
  const maxLabelChars = vw < 640 ? 16 : vw < 1024 ? 26 : 38;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getRiCorridors(clientId, { sort, from, to })
      .then((r) => { if (!cancelled) { setRows(r?.data || []); setLoading(false); } })
      .catch(() => { if (!cancelled) { setRows([]); setFailed(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [clientId, sort, from, to, reloadKey]);

  useEffect(() => { setPage(1); }, [sort, from, to]);

  // Collapse rows whose display labels collide. The canonical key flattens
  // hyphen/space/punct variation so e.g. raw "I-40 W" ("I-40 W") and "I_40_W"
  // (formatted "I 40 W") merge into one row, sharing volume + weight-averaged
  // booking value. Display uses the formatted label of the first row seen.
  const topTen = useMemo(() => {
    if (!rows) return [];
    // Strict canonical: collapse non-alphanumerics AND insert a space at every
    // letter↔digit boundary so "I-40 W", "I 40 W", "I_40_W", and "I40 W" all
    // canonicalize to "I 40 W" and merge into one row.
    const canonical = (s) => String(s)
      .replace(/([A-Za-z])(\d)/g, '$1 $2')
      .replace(/(\d)([A-Za-z])/g, '$1 $2')
      .replace(/[^A-Za-z0-9]+/g, ' ')
      .trim()
      .toUpperCase();
    const byKey = new Map();
    for (const r of rows) {
      const label = formatCorridorLabel(r.corridor);
      const key = canonical(label);
      if (!byKey.has(key)) {
        byKey.set(key, {
          corridor: r.corridor,
          corridor_label: label,
          volume: 0,
          avg_value_num: 0,
          avg_value_den: 0,
        });
      }
      const agg = byKey.get(key);
      const v = Number(r.volume) || 0;
      agg.volume += v;
      if (r.avg_booking_value && v > 0) {
        agg.avg_value_num += Number(r.avg_booking_value) * v;
        agg.avg_value_den += v;
      }
    }
    return [...byKey.values()]
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 10)
      .map((agg) => ({
        corridor: agg.corridor,
        corridor_label: agg.corridor_label,
        volume: agg.volume,
        avg_value: agg.avg_value_den > 0
          ? Math.round(agg.avg_value_num / agg.avg_value_den)
          : 0,
      }))
      .map((row) => ({ ...row, corridor_label: `${row.corridor_label} (${row.volume.toLocaleString()})` }));
  }, [rows]);

  const totalPages = rows ? Math.max(1, Math.ceil(rows.length / TABLE_PAGE_SIZE)) : 1;
  const visibleRows = useMemo(() => {
    if (!rows) return [];
    const start = (page - 1) * TABLE_PAGE_SIZE;
    return rows.slice(start, start + TABLE_PAGE_SIZE);
  }, [rows, page]);

  if (loading) return <RiLoading />;
  if (failed) return <RiError onRetry={retry} />;
  if (!rows || rows.length === 0) return <RiEmpty message="No corridors in this date range — need at least 2 routes per corridor." />;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-center justify-between mb-1 max-[499px]:flex-col max-[499px]:items-stretch max-[499px]:gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Top 10 Corridors</h3>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 dark:text-gray-400">Sort by:</span>
            <button type="button" onClick={() => updateParam('sort', 'volume')}
              title="Sort corridors by guest volume — the number of routed guests driving each corridor"
              className={`px-2 py-1 rounded ${sort === 'volume' ? 'bg-[#32B1FF] text-white' : 'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}>Volume</button>
            <button type="button" onClick={() => updateParam('sort', 'cce')}
              title="Sort corridors by Revenue (CCE = Corridor Contribution to Expected revenue) — each corridor's estimated share of expected booking revenue"
              className={`px-2 py-1 rounded ${sort === 'cce' ? 'bg-[#32B1FF] text-white' : 'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}>Revenue (CCE)</button>
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Highest-volume guest drive corridors. Gray bar = guest count; green bar = avg booking value.</p>
        <div className="w-full" style={{ height: Math.max(320, topTen.length * 36 + 80) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topTen} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.15)" />
              <XAxis type="number" xAxisId="vol" tick={{ fontSize: 11 }} orientation="bottom" />
              <XAxis type="number" xAxisId="val" tick={{ fontSize: 11 }} orientation="top" tickFormatter={(v) => fmtUsd(v)} />
              <YAxis
                type="category"
                dataKey="corridor_label"
                width={yAxisWidth}
                interval={0}
                tick={({ x, y, payload }) => {
                  const v = payload?.value ?? '';
                  const display = typeof v === 'string' && v.length > maxLabelChars
                    ? `${v.slice(0, maxLabelChars - 1).trimEnd()}…`
                    : v;
                  return (
                    <text x={x} y={y} dy={4} textAnchor="end" fontSize={yAxisFont} className="fill-gray-500 dark:fill-gray-400">
                      {display}
                    </text>
                  );
                }}
              />
              <ThemedChartTooltip
                labelFormatter={(label) => String(label).replace(/\s*\([\d,]+\)\s*$/, '')}
                formatter={(v, n) => n === 'Avg Booking Value' ? (Number(v) > 0 ? fmtUsd(v, { cents: true }) : 'No bookings') : v}
              />
              <Legend />
              <Bar xAxisId="vol" dataKey="volume" name="Volume" fill="#9CA3AF" />
              <Bar xAxisId="val" dataKey="avg_value" name="Avg Booking Value" fill="#10B981" minPointSize={2}>
                <LabelList
                  dataKey="avg_value"
                  position="right"
                  formatter={(v) => (Number(v) > 0 ? '' : 'No bookings')}
                  style={{ fontSize: 10, fill: '#9CA3AF' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 overflow-x-auto responsive-card-table-wrapper">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">All Corridors</h3>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {rows.length.toLocaleString()} total · page {page} / {totalPages}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Every corridor with at least one routed guest. Sort by volume or revenue (CCE = corridor contribution to expected revenue).</p>
        <div className={RI_TABLE_FRAME}>
          <table className="w-full text-sm responsive-card-table">
          <thead className={RI_THEAD}>
            <tr>
              <th className={`${RI_TH} text-left`}>Corridor</th>
              <th className={`${RI_TH} text-right`}>Volume</th>
              <th className={`${RI_TH} text-right`}>Avg hrs</th>
              <th className={`${RI_TH} text-right`}>Avg mi</th>
              <th className={`${RI_TH} text-right`}>Avg fuel</th>
              <th className={`${RI_TH} text-right`}>Congestion</th>
              <th className={`${RI_TH} text-right`}>Mile range</th>
              <th className={`${RI_TH} text-right`}>Bookings</th>
              <th className={`${RI_TH} text-right`}>Avg value</th>
              <th className={`${RI_TH} text-right`}>Conv %</th>
              <th className={`${RI_TH} text-right`}>CCE</th>
            </tr>
          </thead>
          <tbody className="text-gray-700 dark:text-gray-300">
            {visibleRows.map((r) => {
              // No corridor drawer exists (and no backend detail for one), so a row is
              // display-only: hover highlight, no click. Rows that open a drawer use
              // RI_ROW_CLICKABLE + clickableRowProps (see the state table).
              const noSessions = r.conversion_note === NO_BOOKING_SESSIONS;
              return (
                <tr key={r.corridor} className={RI_ROW}>
                  <td className={`${RI_TD} font-medium text-gray-900 dark:text-white`} data-label="Corridor">
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <span>{formatCorridorLabel(r.corridor)}</span>
                      {r.confidence === 'emerging' && (
                        <HoverTooltip
                          triggerClassName="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 cursor-help"
                          content={emergingTooltipContent(r)}
                        >
                          Emerging
                        </HoverTooltip>
                      )}
                    </span>
                  </td>
                  <td className={`${RI_TD} text-right tabular-nums`} data-label="Volume">{r.volume != null ? Number(r.volume).toLocaleString() : '—'}</td>
                  <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg hrs">{fmt1(r.avg_hours)}</td>
                  <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg mi">{r.avg_miles != null ? Math.round(Number(r.avg_miles)).toLocaleString() : '—'}</td>
                  <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg fuel">{fmt1(r.avg_fuel)}</td>
                  <td className={`${RI_TD} text-right tabular-nums ${r.avg_congestion_min > 30 ? 'text-red-600 dark:text-red-400' : ''}`} data-label="Congestion">
                    {r.avg_congestion_min != null ? `${Math.round(Number(r.avg_congestion_min))} min` : '—'}
                  </td>
                  <td className={`${RI_TD} text-right tabular-nums text-gray-500 dark:text-gray-400`} data-label="Mile range">
                    {r.min_miles != null && r.max_miles != null
                      ? `${Math.round(Number(r.min_miles)).toLocaleString()}–${Math.round(Number(r.max_miles)).toLocaleString()} mi`
                      : '—'}
                  </td>
                  <td className={`${RI_TD} text-right tabular-nums ${r.bookings > 0 ? 'font-bold text-gray-900 dark:text-white' : ''}`} data-label="Bookings">{Number(r.bookings ?? 0).toLocaleString()}</td>
                  <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg value">{fmtUsd(r.avg_booking_value ?? 0, { cents: true })}</td>
                  <td className={`${RI_TD} text-right tabular-nums font-medium ${noSessions ? 'text-gray-400 dark:text-gray-500' : convColor(convPctFromRow(r))}`} data-label="Conv %">
                    {noSessions ? <NoSessionsCell /> : `${fmt1(convPctFromRow(r))}%`}
                  </td>
                  <td className={`${RI_TD} text-right tabular-nums ${noSessions ? 'text-gray-400 dark:text-gray-500' : ''}`} data-label="CCE">
                    {noSessions ? <NoSessionsCell /> : fmt1(r.cce)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
        <RiSparseNotice count={rows.length} />
        <RiPagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={rows.length} itemLabel="corridor" />
      </div>
    </div>
  );
}
